import { useMutation } from '@apollo/client';
import { notification } from 'antd';
import { INPUT_TYPE } from 'components/common/Input/types';
import { usStatesSelectOptions } from 'constants/usStates';
import { get } from 'lodash';
import React, { useMemo } from 'react';
import WithAddableInputFormTemplate from 'routes/Profile/components/ProfileContent/WithAddableInputFormTemplate';
import { INewInitalFieldsProps } from 'routes/Profile/components/ProfileContent/WithAddableInputFormTemplate/types';
import { UPDATE_USER } from 'services/graphql/mutations/users';
import { GeneralFunction } from 'types/common';
import { IContactType, IOtherEmailType } from 'types/user';
import { generateContactsInputFieldsProps, generateOtherEmailsInputFieldsProps } from 'utils';
import * as Yup from 'yup';

import { IStepComponentProps } from './types';

const GeneralInformationWizard: React.FC<IStepComponentProps> = ({
  userInfo,
  buttonLabel,
  onClickNext,
  isActive,
  saveButtonContainerClass,
  refetch,
}) => {
  const otherEmailsInputProps = userInfo ? generateOtherEmailsInputFieldsProps(userInfo.otherEmails) : {};
  const contactsInputProps = userInfo ? generateContactsInputFieldsProps(userInfo.contacts) : {};

  const otherEmailsInputFields = get(otherEmailsInputProps, 'inputFieldProps', {});
  const otherEmailsSchema = get(otherEmailsInputProps, 'validationSchema', {});

  const contactsInputFields = get(contactsInputProps, 'inputFieldProps', {});
  const contactsSchema = get(contactsInputProps, 'validationSchema', {});

  const [updateUserMutation, { loading }] = useMutation(UPDATE_USER);

  const addressSchema = {
    address1: Yup.string().max(46, 'Maximum length is 46').required('Required'),
    address2: Yup.string().max(46, 'Maximum length is 46').notRequired(),
    city: Yup.string().max(50, 'Maximum length is 50').required('Required'),
    state: Yup.string().nullable().required('Required'),
    zip: Yup.string()
      .matches(/(^\d{5}$)|(^\d{9}$)|(^\d{5}-\d{4}$)/, 'Wrong format')
      .required('Required'),
  };

  const validationSchema = {
    ...otherEmailsSchema,
    ...contactsSchema,
    ...addressSchema,
  };

  const inputFields: INewInitalFieldsProps[] = [
    {
      ...otherEmailsInputFields,
      sectionTitle: 'Emails',
      sizes: [12, 12],
      draggable: false,
    },
    {
      ...contactsInputFields,
      sectionTitle: 'Phone Numbers',
      sizes: [6, 6, 12],
      draggable: false,
    },
    {
      sectionTitle: 'Address',
      inputProps: [
        {
          label: 'Address Line 1',
          name: 'address1',
          addressSuggestion: true,
          addressSuggestionSubFields: {
            address1: 'address1',
            city: 'city',
            zip: 'zip',
            state: 'state',
          },
        },
        { label: 'Address Line 2', name: 'address2' },
        { label: 'City', name: 'city' },
        {
          label: 'Zip code',
          name: 'zip',
          sideFields: [
            {
              size: 12,
              label: 'State',
              name: 'state',
              type: INPUT_TYPE.SELECT,
              inputOptions: usStatesSelectOptions,
            },
          ],
        },
      ],
    },
  ];

  const { contacts, otherEmails } = useMemo(() => userInfo, [userInfo]);

  const updateUser = async (v: any, callbackOnSuccess?: GeneralFunction) => {
    const otherEmailsNew = v?.['otherEmails-address']?.reduce(
      (acc: IOtherEmailType[], email: string, index: number) => {
        if (!email) return acc;
        const priority = index - 1; // otherEmails: [empty, OtherEMail1, otherEmail2,...] => due to "email" field different from 'otherEmails'
        acc.push({
          address: email,
          type: v?.['otherEmails-type']?.[index] || '',
          id: otherEmails[priority]?.id || undefined,
          priority,
        });
        return acc;
      },
      [],
    );

    const contactsNew = v?.['contacts-number']?.reduce((acc: IContactType[], contact: string, index: number) => {
      if (!contact) return acc;
      acc.push({
        number: contact,
        type: v?.['contacts-type']?.[index] || '',
        numberType: v?.['contacts-numberType']?.[index] || '',
        id: contacts[index]?.id || undefined,
        priority: index,
      });
      return acc;
    }, []);
    const { firstName, lastName, city, address1, address2, state, zip } = v;

    const updatedDoc = {
      firstName,
      lastName,
      city,
      address1,
      address2,
      state,
      zip,
      emailInputs: otherEmailsNew,
      contactInputs: contactsNew,
    };
    try {
      await updateUserMutation({
        variables: {
          updatedDoc,
          filter: { id: userInfo.id },
        },
      });
      callbackOnSuccess?.();
      refetch?.();
    } catch (err) {
      notification.error({ message: 'Update failed! Please try again.', duration: 2 });
    }
  };

  const onSubmit = (v: any) => {
    updateUser(v, onClickNext);
  };

  // const companyField: INewInitalFieldsProps[] = [
  //   {
  //     sectionTitle: 'Company',
  //     labelProps: ['Company Name'],
  //     inputProps: [
  //       {
  //         name: 'company',
  //         defaultValue: userInfo.company?.name || '',
  //         disabled: true,
  //       },
  //     ],
  //   },
  // ];

  return (
    <WithAddableInputFormTemplate
      initialFields={inputFields}
      userData={userInfo}
      validation={validationSchema}
      onSubmit={onSubmit}
      submitButtonLabel={buttonLabel}
      portalButtonClassName={saveButtonContainerClass}
      hideSaveButton={!isActive}
      showNotificationOnSuccess={false}
      refetchUserData={refetch}
      loading={loading}
    />
  );
};

export default React.memo(GeneralInformationWizard);
